import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { ArrowLeft, Save, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { ContactForm } from "@/components/program/ContactForm";
import { LogoUpload } from "@/components/program/LogoUpload";
import { APIClient } from "@/api";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import countries from "@/constants/array/country_city.json";
import { LoaderButton } from "@/components/ui/loader-button";
import VirtualizedCitySelect from "@/components/ui/virtualized-city-select";

const OffersProgramSchema = Yup.object().shape({
  id: Yup.string().optional().nullable(),
  logo: Yup.mixed().required("Logo is required"),
  name: Yup.string()
    .required("Program Name is required")
    .matches(
      /^[a-zA-Z0-9\s\-':.]+$/,
      "Program name can only contain letters, numbers, spaces, and the following special characters: - ' : ."
    ),
  address: Yup.string().required("Program Location is required"),
  code: Yup.string()
    .required("Program code is required")
    .matches(
      /^[a-zA-Z0-9]+$/,
      "Program code can only contain letters and numbers (no special characters)"
    ),
  websiteUrl: Yup.string()
    .transform((value) => (value ? value : ""))
    .test("is-valid-url", "Website URL must be a valid HTTP URL format", (v) =>
      !v ? true : Yup.string().url().isValidSync(v)
    ),
  country: Yup.string().required("Country is required"),
  city: Yup.string().required("City is required"),
  contacts: Yup.array().of(
    Yup.object().shape({
      name: Yup.string()
        .required("Contact name is required")
        .matches(/^[A-Za-z ]+$/, "Only letters and spaces are allowed"),
      email: Yup.string()
        .required("Email is required")
        .email("Email must be a valid email address"),
      role: Yup.string()
        .required("Role is required")
        .matches(/^[A-Za-z ]+$/, "Only letters and spaces are allowed"),
      countryCode: Yup.string(),
      phoneNumber: Yup.string()
        .required("Phone is required")
        .matches(/^\d+$/, "Phone must contain digits only")
        .min(7, "Phone number must be at least 7 digits")
        .max(15, "Phone number cannot exceed 15 digits"),
    })
  ),
});

const OffersCreateProgram = () => {
  const basePath = "/offers";
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [codeVerificationStatus, setCodeVerificationStatus] = useState<
    "available" | "taken" | null
  >(null);
  const form = useForm<any>({
    resolver: yupResolver(OffersProgramSchema),
    defaultValues: {
      id: null,
      logo: null,
      name: "",
      code: "",
      programType: "",
      clientId: "",
      address: "",
      paymentCurrency: "",
      costPerPoint: null,
      loyaltyPointsName: "",
      cashbackPercentage: null,
      txnCommissionPctChargedToPrg: null,
      vatNumber: "",
      invoiceFrequency: "",
      websiteUrl: "",
      domain: "",
      city: "",
      contacts: [
        {
          name: "",
          email: "",
          countryCode: "+971",
          phoneNumber: "",
          role: "",
        },
      ],
    },
  });

  const verifyProgramCode = async () => {
    const programCode = form.getValues("code");
    if (!programCode || programCode.trim() === "") {
      toast.error("Please enter a program code first");
      return;
    }

    setIsVerifyingCode(true);
    setCodeVerificationStatus(null);

    // No API call in Offer Point for now; allow local confirmation only.
    setCodeVerificationStatus("available");
    toast.success("Program code marked as available");
    setIsVerifyingCode(false);
  };

  const onSubmit = async (formData) => {
    // Check if program code is verified as available
    if (codeVerificationStatus !== "available") {
      toast.error(
        "Please verify that the program code is available before submitting"
      );
      return;
    }
    setIsSubmitting(true);
    try {
      const { contacts } = formData;

      const formDataToSend = new FormData();
      if (formData?.logo) {
        formDataToSend.append("programOfferLogo", formData.logo);
      }

      const selectedCountryName =
        selectedCountry?.name ||
        (() => {
          try {
            const parsedCountry = formData?.country
              ? JSON.parse(formData.country)
              : null;
            return parsedCountry?.name || "";
          } catch {
            return "";
          }
        })();

      const programOfferData = {
        id: formData?.id,
        programCode: formData?.code,
        programName: formData?.name,
        websiteUrl: formData?.websiteUrl || "",
        address: formData?.address,
        country: selectedCountryName,
        city: formData?.city,
      };

      const blob = new Blob([JSON.stringify(programOfferData)], {
        type: "application/json",
      });
      formDataToSend.append("programOfferVO", blob);

      const { data } = await APIClient(
        "/program/createOrUpdateProgramForOffer",
        formDataToSend,
        { requestType: "post", contentType: "multipart/form-data" }
      );

      const programId = data?.id ?? data?.programId ?? formData?.id;
      if (!programId) {
        throw new Error("Program ID not returned from create/update API");
      }

      await Promise.all(
        contacts.map((contact) =>
          APIClient(
            "/program/createOrUpdateProgramOfferContact",
            {
              programId,
              contactName: contact?.name,
              contactEmail: contact?.email,
              contactRole: contact?.role,
              programContactPhoneVOList: [
                {
                  phoneCountryCode: contact?.countryCode,
                  phoneNumber: contact?.phoneNumber,
                },
              ],
            },
            { requestType: "post" }
          )
        )
      );

      toast.success("Program created successfully");
      navigate(`${basePath}/programs`);
    } catch (error) {
      console.error("Error creating offer program:", error);
      toast.error("Failed to create program");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" asChild className="bg-white">
          <Link to={`${basePath}/programs`}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Link>
        </Button>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-rose-600 mb-1">
            Offer Point
          </p>
          <h1 className="text-2xl font-bold text-gray-800">
            Create New Program
          </h1>
          <p className="text-gray-500">Create a new loyalty program</p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          {/* General Information */}
          <Card className="border-0 shadow-sm overflow-hidden bg-white w-full">
            <CardHeader className="card-header-compact bg-gray-50">
              <CardTitle className="text-lg font-medium text-gray-800">
                General Information
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
                {/* Logo Upload */}
                <LogoUpload form={form} />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Program Name*</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Emirates Skywards"
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value.replace(
                              /[^a-zA-Z0-9\s\-':.]/g,
                              ""
                            );
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        The name of your loyalty program
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Program Code*</FormLabel>
                      <FormControl>
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <Input
                              placeholder="e.g. SKYW001"
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                // Reset verification status when code changes
                                setCodeVerificationStatus(null);
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={verifyProgramCode}
                              disabled={isVerifyingCode || !field.value}
                              className="shrink-0"
                            >
                              {isVerifyingCode ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Verifying...
                                </>
                              ) : (
                                "Verify"
                              )}
                            </Button>
                          </div>
                          {codeVerificationStatus && (
                            <div className="flex items-center gap-2 text-sm">
                              {codeVerificationStatus === "available" ? (
                                <>
                                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                                  <span className="text-green-600">
                                    Program code is available
                                  </span>
                                </>
                              ) : (
                                <>
                                  <XCircle className="h-4 w-4 text-red-600" />
                                  <span className="text-red-600">
                                    Program code already exists
                                  </span>
                                </>
                              )}
                            </div>
                          )}
                          {codeVerificationStatus !== "available" && (
                            <p className="text-sm text-gray-500">
                              Program code must be verified as available before
                              you can submit the form.
                            </p>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="websiteUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Website URL</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. www.example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Address*</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Enter company address"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                        <FormLabel>Country*</FormLabel>
                        <Select
                          onValueChange={(value) => {
                            const selected = JSON.parse(value);
                            setSelectedCountry(selected);
                            field.onChange(value);
                            form.setValue("city", "");
                          }}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select Country" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {(countries as any)?.map((country) => (
                              <SelectItem
                                key={country?.name}
                                value={JSON.stringify(country)}
                              >
                                {country?.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City*</FormLabel>
                      <FormControl>
                        <VirtualizedCitySelect
                          cities={selectedCountry?.cities || []}
                          value={field.value}
                          onValueChange={field.onChange}
                          placeholder="Select City"
                          disabled={!selectedCountry?.cities?.length}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="border-0 shadow-sm overflow-hidden bg-white">
            <CardHeader className="card-header-compact bg-gray-50">
              <CardTitle className="text-lg font-medium text-gray-800">
                Contact Information
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Add one or more contact persons for this program
              </p>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <ContactForm form={form} />
            </CardContent>
          </Card>

          <Separator className="my-8" />

          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`${basePath}/programs`)}
              className="border-rose-200 text-rose-900 hover:bg-rose-50"
            >
              Cancel
            </Button>
            <LoaderButton
              type="submit"
              loading={isSubmitting}
              className="bg-rose-600 text-white hover:bg-rose-700 focus-visible:ring-rose-500"
            >
              <Save className="h-4 w-4 mr-2" /> Create Program
            </LoaderButton>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default OffersCreateProgram;
